import React, { useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CrmShell } from "@/components/portal/crm/CrmShell";
import { InstallPrompt } from "@/components/portal/InstallPrompt";
import { ChatWidget } from "@/components/portal/ChatWidget";
import { GuidedTour } from "@/components/portal/GuidedTour";
import { DeskDecision } from "@/components/portal/sys/DeskDecision";
import { NowDialog } from "@/components/portal/sys/NowDialog";
import { NOW, nowOf } from "@/lib/stages";
import { toast } from "sonner";

const TOUR = (me, hasLeads) => [
  { selector: '[data-testid="lender-stats"]', title: "Your desk at a glance",
    body: "Everything routed to you, what is waiting on your decision, what you funded and how fast you decide." },
  { selector: '[data-testid="lender-dealflow"]', title: "Cases land here",
    body: "Each row is a full application. Open one for the trading history, the documents and the thread with Connexis." },
  { selector: '[data-testid^="lender-lead-status-"]', title: "Move it straight from the row",
    body: "With lenders → Under assessment → Offer issued → Offer accepted → Funded. Offer issued asks for your terms, Declined asks why. Underneath, post a Right now update — Trying to reach the client, Call back booked — and the introducer sees it in their portal." },
  { selector: '[data-testid="lender-tab-criteria"]', title: "Tune what you receive",
    body: "Deal sizes, sectors, finance types and turnaround. The tighter your criteria, the better the fit on everything we send." },
  { selector: '[data-testid="lender-tab-crm"]', title: "Straight into your CRM",
    body: "Connect HubSpot, Salesforce or Pipedrive and every routed case arrives as a live record." },
  ...(me?.agreement?.status !== "signed"
    ? [{ selector: '[data-testid="agreement-callout"]', title: "Sign once, then you're live",
         body: "Your panel agreement takes about twenty seconds — type your name, done." }]
    : []),
  { title: hasLeads ? "Ready to review your first case?" : "That's the desk",
    finishLabel: hasLeads ? "Open the newest case" : "Got it",
    body: hasLeads
      ? "Moving a case to Under assessment tells the introducer somebody is actually looking at it."
      : "Nothing routed yet. As soon as a case matches your criteria it appears here." },
];

export default function LenderPortal() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);
  const [decision, setDecision] = useState(null);
  const [nowAsk, setNowAsk] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [m, L] = await Promise.all([api.get("/lender/me"), api.get("/lender/leads")]);
      setMe(m.data); setLeads(L.data.items || []);
      if (!isAdmin && !m.data?.tour_completed) setTourOpen(true);
    } catch { /* the shell still renders */ }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { reload(); }, [reload]);

  // Keeps the live lines current without a page refresh.
  useEffect(() => {
    const t = setInterval(() => {
      if (!document.hidden) api.get("/lender/leads").then((r) => setLeads(r.data.items || [])).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let t;
    const tick = async () => {
      try { setUnread((await api.get("/hub/chat/unread")).data.unread || 0); } catch { /* silent */ }
      t = setTimeout(tick, 25000);
    };
    tick();
    return () => clearTimeout(t);
  }, []);

  const updateStatus = async (leadId, status, extras = {}) => {
    try {
      const { clear, ...body } = extras;
      await api.patch(`/lender/leads/${leadId}/status`, { status, ...body });
      await reload();
      toast.success(status === "declined"
        ? "Noted — the Connexis desk has the reason and is finding it another home"
        : NOW[status] ? "Update posted — the introducer has been emailed"
          : status === "preparing_offer" ? "Preparing offer — the introducer has been told"
          : extras.clear ? "Update cleared" : "Stage updated, introducer notified");
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const onStatusPick = (leadId, status) => {
    if (NOW[status]) setNowAsk({ id: leadId, kind: status, current: nowOf(leads.find((l) => l.id === leadId)) });
    else if (["declined", "offer_made"].includes(status)) setDecision({ id: leadId, mode: status });
    else updateStatus(leadId, status);
  };
  const onNowClear = (leadId) => updateStatus(leadId, "in_review", { clear: true, keep_stage: true });

  const closeTour = async () => {
    setTourOpen(false);
    if (me && !me.tour_completed) {
      setMe((m) => ({ ...m, tour_completed: true }));
      try { await api.post("/lender/tour-complete"); } catch { /* non-fatal */ }
    }
  };

  const waiting = leads.filter((l) => l.status === "sent").length;

  const groups = [
    { title: "Desk", items: [
      { to: "/lender", label: "Cases", icon: "layers", end: true, count: leads.length, testId: "lender-tab-leads" },
      { to: "/lender/messages", label: "Messages", icon: "mail", count: unread, testId: "lender-tab-messages" },
      { to: "/lender/connie", label: "Ask Connie", icon: "sparkle", testId: "lender-tab-connie" },
    ] },
    { title: "Appetite", items: [
      { to: "/lender/criteria", label: "Your criteria", icon: "bars", testId: "lender-tab-criteria" },
      { to: "/lender/fees", label: "Completion fees", icon: "card", testId: "lender-tab-fees" },
      { to: "/lender/crm", label: "CRM delivery", icon: "plug", testId: "lender-tab-crm" },
    ] },
    { title: "Account", items: [
      { to: "/lender/documents", label: "Documents", icon: "doc", testId: "lender-tab-documents" },
      { to: "/lender/settings", label: "Desk settings", icon: "cog", testId: "lender-tab-settings" },
      { to: "/lender/feedback", label: "Help shape Connexis", icon: "idea", testId: "lender-tab-feedback" },
    ] },
  ];

  const tabs = [
    { to: "/lender", label: "Cases", icon: "layers", end: true },
    { to: "/lender/criteria", label: "Criteria", icon: "bars" },
    { to: "/lender/fees", label: "Fees", icon: "card" },
    { to: "/lender/messages", label: "Desk", icon: "mail" },
    { to: "/lender/connie", label: "Connie", icon: "sparkle" },
    { to: "/lender/settings", label: "Settings", icon: "cog" },
  ];

  const pills = [
    { label: me?.leads_paused ? "Paused" : me?.status === "active" ? "Active desk" : me?.status || "Invited",
      tone: me?.leads_paused ? "pale" : me?.status === "active" ? "good" : "pale" },
    { label: me?.company_type || "Credit team", tone: "pale" },
  ];

  if (loading) {
    return <div className="cxp"><div className="empty" style={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>Loading your desk…</div></div>;
  }

  return (
    <>
      <CrmShell
        kind="lender"
        railLabel="Lender desk"
        testId="lender-portal"
        orgName={me?.company_name || "Your desk"}
        orgSub="Panel lender"
        pills={pills}
        groups={groups}
        tabs={tabs}
        crumbRoot="Lender desk"
        topRight={
          <button className="btn btn-ghost btn-sm" onClick={() => setTourOpen(true)} data-testid="lender-replay-tour">
            Guide me
          </button>
        }
      >
        <Outlet context={{ me, setMe, leads, reload, isAdmin, unread, onStatusPick, onNowClear, updateStatus }} />

        <DeskDecision
          open={!!decision}
          mode={decision?.mode}
          onClose={() => setDecision(null)}
          onConfirm={async (payload) => {
            if (!decision) return;
            await updateStatus(decision.id, decision.mode, payload);
            setDecision(null);
          }}
        />
        {nowAsk && (
          <NowDialog
            kind={nowAsk.kind}
            current={nowAsk.current}
            testId="lender-now-dialog"
            onClose={() => setNowAsk(null)}
            onConfirm={async (payload) => {
              await updateStatus(nowAsk.id, nowAsk.kind, payload);
              setNowAsk(null);
            }}
          />
        )}
      </CrmShell>

      <GuidedTour steps={TOUR(me, leads.length > 0)} open={tourOpen} onClose={closeTour} onFinish={closeTour} />
      <ChatWidget portalLabel="Lender desk" />
      <InstallPrompt />
    </>
  );
}
